// Non-survey CAD service catalog used by the /cad-techies page.
// Sourced/synthesized from cadtechies.com, landdrafters.com, draftingsources.com,
// indovance.com, cadoutsourcing.net, hitechbimservices.com, chemionix.com, digitiseit.co.in
//
// Survey-related disciplines (ALTA, boundary, topo, plats, ROW, GIS, as-built etc.)
// are intentionally NOT included here — those live under /services and /survey-techies.

export const CAD_SERVICES = [
  {
    slug: "architectural-cad",
    name: "Architectural CAD Drafting",
    short:
      "Floor plans, elevations, sections, working drawings & permit-set documentation.",
    emoji: "🏛️",
    hero:
      "Production-grade architectural drafting from concept sketches to permit-ready construction documents.",
    deliverables: [
      "Floor plans, elevations & sections (DWG + PDF)",
      "Working drawings & detailed sheets",
      "Permit / building-department submission sets",
      "Reflected ceiling & finish schedules",
    ],
    subpages: [
      { slug: "residential", name: "Residential Architectural", body: "Single-family, multi-family and townhouse permit & construction drawings." },
      { slug: "commercial", name: "Commercial Architectural", body: "Retail, office, hotel and mixed-use fit-out and base-building drawings." },
      { slug: "millwork-joinery", name: "Millwork & Joinery", body: "Custom cabinetry, casework and architectural millwork shop drawings." },
      { slug: "permit-sets", name: "Permit & Submission Sets", body: "Code-compliant building-department submittal packages." },
    ],
  },
  {
    slug: "structural-cad",
    name: "Structural CAD Detailing",
    short:
      "Rebar, steel, precast, prefab & fabrication shop drawings to project standards.",
    emoji: "🏗️",
    hero:
      "Engineering-grade structural detailing for steel, concrete, precast and prefabricated structures.",
    deliverables: [
      "Rebar bar-bending schedules (BBS)",
      "Steel connection & shop fabrication drawings",
      "Precast / prefab erection sheets",
      "Structural design analysis support drawings",
    ],
    subpages: [
      { slug: "rebar-detailing", name: "Rebar Detailing", body: "ACI/BS/Eurocode-compliant rebar shop drawings & BBS." },
      { slug: "steel-detailing", name: "Steel Detailing", body: "AISC/CISC steel connection, anchor-bolt & erection drawings." },
      { slug: "precast-prefab", name: "Precast / Prefab", body: "Precast panel, double-tee, hollow-core and prefab module drawings." },
      { slug: "fabrication-shop-drawings", name: "Fabrication Shop Drawings", body: "Detailed shop drawings for fabricators with cut lists." },
      { slug: "structural-3d", name: "Structural 3D Modeling", body: "Tekla Structures and Revit Structure 3D models." },
    ],
  },
  {
    slug: "mechanical-cad",
    name: "Mechanical CAD & 3D Modeling",
    short:
      "PFD, P&ID, sheet-metal design, mechanical 3D modeling & assembly drawings.",
    emoji: "⚙️",
    hero:
      "Mechanical engineering drafting and 3D modeling for product design, manufacturing and process plants.",
    deliverables: [
      "PFD & P&ID drawings (ISA standard)",
      "Sheet-metal flat patterns & assemblies",
      "Mechanical 3D models (SolidWorks / Inventor)",
      "Manufacturing & assembly drawings",
    ],
    subpages: [
      { slug: "pfd-pid", name: "PFD / P&ID Drafting", body: "Process flow diagrams and piping & instrumentation diagrams for refineries, plants and pharma." },
      { slug: "sheet-metal-design", name: "Sheet-Metal Design", body: "Unfolded flat patterns, weldments, brackets and enclosures." },
      { slug: "3d-mechanical-modeling", name: "3D Mechanical Modeling", body: "Parametric 3D models in SolidWorks, Inventor and Creo." },
      { slug: "assembly-drawings", name: "Assembly Drawings", body: "Exploded views, BOMs and assembly instructions." },
    ],
  },
  {
    slug: "electrical-cad",
    name: "Electrical CAD Drafting",
    short:
      "Schematics, panel layouts, single-line diagrams & cable schedules.",
    emoji: "⚡",
    hero:
      "Electrical engineering drawings from single-line diagrams to control panel layouts and cable routing.",
    deliverables: [
      "Single-line & schematic diagrams",
      "Panel layout & wiring drawings",
      "Cable schedules & tray layouts",
      "Lighting & power distribution plans",
    ],
    subpages: [
      { slug: "schematic-diagrams", name: "Schematic Diagrams", body: "Power, control and instrumentation schematics." },
      { slug: "panel-layouts", name: "Panel & Wiring Layouts", body: "MCC, switchgear and control-panel layouts." },
      { slug: "lighting-design", name: "Lighting & Power Layouts", body: "Photometric layouts, lighting fixture schedules and power-distribution drawings." },
    ],
  },
  {
    slug: "mep-bim",
    name: "MEP & BIM Coordination",
    short:
      "Revit MEP, clash detection, 3D coordination & BIM family creation.",
    emoji: "🧊",
    hero:
      "Mechanical-Electrical-Plumbing modeling and clash-free BIM coordination for design and construction teams.",
    deliverables: [
      "Revit MEP discipline models (LOD 200–400)",
      "Navisworks clash-detection reports",
      "BIM family library creation",
      "As-built BIM / Scan-to-BIM",
    ],
    subpages: [
      { slug: "revit-mep", name: "Revit MEP Modeling", body: "Discipline-coordinated mechanical, electrical and plumbing Revit models." },
      { slug: "clash-detection", name: "Clash Detection", body: "Navisworks clash reports, resolution coordination and federated reviews." },
      { slug: "scan-to-bim", name: "Scan-to-BIM", body: "Point-cloud conversion to LOD-accurate Revit models." },
      { slug: "bim-family-creation", name: "BIM Family Creation", body: "Parametric Revit family library development." },
    ],
  },
  {
    slug: "plumbing-piping",
    name: "Plumbing & Piping",
    short:
      "Piping isometrics, plumbing layouts, stack diagrams & fixture schedules.",
    emoji: "🚰",
    hero:
      "Piping and plumbing design drafting for residential, commercial and industrial process projects.",
    deliverables: [
      "Piping isometric drawings",
      "Plumbing layout & riser diagrams",
      "Stack & vent diagrams",
      "Fixture schedules & material take-offs",
    ],
    subpages: [
      { slug: "piping-isometrics", name: "Piping Isometrics", body: "Spool drawings with weld details, bills of material and ISO sheets." },
      { slug: "plumbing-layouts", name: "Plumbing Layouts", body: "Hot/cold water, waste, vent and stormwater drawings." },
    ],
  },
  {
    slug: "hvac-cad",
    name: "HVAC CAD Drafting",
    short:
      "Duct layouts, equipment schedules, hydronic piping & ventilation drawings.",
    emoji: "🌬️",
    hero:
      "Mechanical HVAC drafting — duct routing, equipment placement and hydronic distribution drawings.",
    deliverables: [
      "Duct layout & sizing drawings",
      "Equipment schedules & connection details",
      "Hydronic piping & control diagrams",
      "Ventilation & smoke-control plans",
    ],
    subpages: [
      { slug: "duct-design", name: "Duct Routing & Sizing", body: "Round and rectangular duct layouts with sizing notes." },
      { slug: "equipment-schedules", name: "Equipment Schedules", body: "AHU, RTU, fan, pump and terminal-unit schedules." },
    ],
  },
  {
    slug: "civil-engineering-drafting",
    name: "Civil Engineering Drafting",
    short:
      "Road, drainage, stormwater & infrastructure drawings (non-survey).",
    emoji: "🛣️",
    hero:
      "Civil engineering production drafting for road networks, drainage and infrastructure design teams.",
    deliverables: [
      "Road plan & profile sheets",
      "Stormwater & drainage drawings",
      "Cross-sections & earthwork volumes",
      "Pavement & marking layouts",
    ],
    subpages: [
      { slug: "road-networks", name: "Road Networks", body: "Plan, profile, geometry and intersection drawings for highway and street projects." },
      { slug: "stormwater-drainage", name: "Stormwater & Drainage", body: "Storm-sewer, detention and BMP drawings." },
      { slug: "urban-engineering", name: "Urban Engineering", body: "Streetscape, urban-utility and right-of-way drawings (non-survey scope)." },
    ],
  },
  {
    slug: "3d-rendering",
    name: "3D Modeling & Rendering",
    short:
      "Architectural visualization, walkthroughs & photoreal renderings.",
    emoji: "🎨",
    hero:
      "Photoreal renderings, 3D walk-throughs and visualization assets for marketing and design review.",
    deliverables: [
      "Photoreal exterior & interior renderings",
      "Animated 3D walk-throughs (MP4)",
      "VR/AR-ready 3D models",
      "360° panoramic stills",
    ],
    subpages: [
      { slug: "architectural-rendering", name: "Architectural Rendering", body: "Photoreal stills for marketing, leasing and approvals." },
      { slug: "3d-walkthroughs", name: "3D Walk-throughs", body: "Animated fly-through and interior walk-through videos." },
      { slug: "vr-ar", name: "VR / AR Models", body: "Real-time VR/AR-ready models for Unreal, Unity and Twinmotion." },
    ],
  },
  {
    slug: "pdf-to-cad",
    name: "PDF-to-CAD & Conversion",
    short:
      "Raster-to-vector, paper-to-CAD, legacy drawing redraws & digitization.",
    emoji: "📄",
    hero:
      "Convert paper drawings, scanned PDFs and image rasters into editable, layered CAD files.",
    deliverables: [
      "Editable DWG / DXF from PDF or paper",
      "Layered, scaled & coordinate-correct files",
      "Raster-to-vector clean-up",
      "Legacy CAD format conversion",
    ],
    subpages: [
      { slug: "raster-to-vector", name: "Raster to Vector", body: "Clean vectorized drawings from scanned blueprints and image files." },
      { slug: "paper-to-cad", name: "Paper to CAD", body: "Field-marked or hand-drawn sketches converted to fully-layered DWG files." },
      { slug: "legacy-redraw", name: "Legacy Drawing Redraws", body: "Old DWG / DGN / RVT version migrations and clean redraws." },
    ],
  },
  {
    slug: "solar-pv-design",
    name: "Solar PV Design",
    short:
      "Rooftop & ground-mount PV layouts, single-line diagrams & permit sets.",
    emoji: "☀️",
    hero:
      "Solar photovoltaic design drafting for residential, commercial and utility-scale projects.",
    deliverables: [
      "PV array layout drawings",
      "Single-line electrical diagrams",
      "Structural attachment details",
      "AHJ-compliant permit submission set",
    ],
    subpages: [
      { slug: "residential-pv", name: "Residential PV", body: "Rooftop solar layouts and permit-ready drawing sets." },
      { slug: "commercial-pv", name: "Commercial PV", body: "Carport, ballasted and ground-mount commercial PV designs." },
    ],
  },
  {
    slug: "signage-design",
    name: "Signage & Pre-Media Design",
    short:
      "Signage shop drawings, packaging artwork & pre-media production.",
    emoji: "🪧",
    hero:
      "Signage structural drawings, packaging artwork and color/pre-media production for FMCG and brand teams.",
    deliverables: [
      "Signage structural 3D & shop drawings",
      "Custom & graphic signage layouts",
      "Packaging artwork & dielines",
      "Color management & pre-media output",
    ],
    subpages: [
      { slug: "structural-signage", name: "Structural Signage", body: "Channel-letter, monument and pylon signage shop drawings." },
      { slug: "graphic-signage", name: "Graphic & Custom", body: "Wayfinding, ADA and custom graphic signage layouts." },
      { slug: "pre-media", name: "Pre-Media / Packaging", body: "Streamlined packaging artwork, color management and image processing for FMCG/CPG brands." },
    ],
  },
];

/* Mega-menu category groups for CAD (non-survey) disciplines. */
export const CAD_SERVICE_CATEGORIES = [
  {
    title: "Architecture & Structural",
    blurb: "Buildings, working drawings & detailing.",
    slugs: ["architectural-cad", "structural-cad", "3d-rendering"],
  },
  {
    title: "MEP, Mechanical & Electrical",
    blurb: "MEP/BIM coordination & engineering CAD.",
    slugs: ["mep-bim", "mechanical-cad", "electrical-cad", "plumbing-piping", "hvac-cad"],
  },
  {
    title: "Civil, Conversion & Specialty",
    blurb: "Infrastructure, redraws & specialty CAD.",
    slugs: ["civil-engineering-drafting", "pdf-to-cad", "solar-pv-design", "signage-design"],
  },
];
